import { useGetAuctionForms } from "apollo/hooks/queries";
import { composeProgress, isLoading } from "apollo/utils";
import {
  FILE_SCOPE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { getPatientName } from "core/model/patients";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormName,
  FileParams,
  FileState,
  Patient,
  QueryProgress,
  ValueOf,
} from "core/types";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import { isFileV2 } from "core/model/files/utils";
import { RefreshCwIcon, SparklesIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";
import { FormFooter } from "./Footer";
import { DocsFormFields } from "./FormFields";
import { HEADER_BANNERS } from "./HeaderBanners";
import { DocsModalSources } from "./Sources";
import { useDocsForm } from "./useDocsForm";

export const DOCS_FORM_FOOTER_SCREENS = {
  DEFAULT: "default",
  DELETE_CONFIRMATION: "delete-confirmation",
  SAVE_CONFIRMATION: "save-confirmation",
  WARN_OPEN_PDF: "warn-open-pdf",
  WARN_SEND_TO_HIS: "warn-send-to-his",
} as const;

export type DocsFormFooterScreen = ValueOf<typeof DOCS_FORM_FOOTER_SCREENS>;

export const useFetchFiles = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  patient: Patient;
  consultation: ClinicalConsultation | undefined;
}) => {
  const [filesProgress, setFilesProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [files, setFiles] = useState<{ name: string; file: FileState }[]>([]);
  const toast = useToast();
  const translations = useTranslations();

  const params: FileParams = auction
    ? `patient_id=${patient.id}&auction_id=${auction.id}`
    : consultation
    ? `clinical_consultation_id=${consultation.id}`
    : `patient_id=${patient.id}`;

  const { getFilesHISProgress, queryProgress, uploadedFilesV2 } =
    useGetFilesFeatures({
      action: undefined,
      externalId: patient.external_id,
      params,
      // is this right? not all forms are files yet so we need this i think
      skipForms: true,
    });

  const filteredUploadedFilesV2 = useMemo(
    () =>
      uploadedFilesV2?.filter((file) =>
        isFileV2(file) ? file.file_scope !== FILE_SCOPE.ELIGIBILITY : true,
      ),
    [uploadedFilesV2],
  );

  useEffect(() => {
    (async () => {
      try {
        setFilesProgress(QUERY_PROGRESS_PENDING);
        const filesData = await Promise.all(
          filteredUploadedFilesV2?.map(async (file) => {
            const props = await getFileNameAndIsNewVersionAvailable({
              canAccessPatientData: true,
              file,
            });

            if (props.shouldBlur) {
              return null;
            }

            if (typeof props.value === "string" && file.updated_at) {
              return { file, name: props.value };
            }

            return null;
          }) ?? [],
        );
        if (filesData.truthy()) {
          setFiles(filesData.truthy());
        }
        setFilesProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        toast({
          color: "danger",
          message: translations.general.error,
        });
        console.error("Error fetching files:", error);
        setFilesProgress(QUERY_PROGRESS_FAILED);
      }
    })();
  }, [filteredUploadedFilesV2]);

  return {
    files,
    filesProgress: composeProgress([
      filesProgress,
      getFilesHISProgress,
      queryProgress,
    ]),
    isLoadingFiles: isLoading(filesProgress),
  };
};

const DocsModalHeader = ({
  canImportData,
  hasPreviousFormValue,
  header,
  importFormData,
  importFormProgress,
  onClose,
  patient,
}: {
  hasPreviousFormValue: boolean;
  canImportData: boolean;
  header: string;
  importFormData: () => Promise<void>;
  importFormProgress: QueryProgress;
  onClose: () => void;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const isFormFilled =
    hasPreviousFormValue || importFormProgress !== QUERY_PROGRESS_NOT_STARTED;

  return (
    <ModalHeader className="flex w-full items-center justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-body-md font-semibold text-gray-600">{`${getPatientName(
          patient,
        )} (${patient.user_id})`}</p>
        <h1 className="text-heading-lg font-bold">{header}</h1>
      </div>
      <div className="flex gap-4">
        <Button
          color="primary"
          id={isFormFilled ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
          isDisabled={!canImportData}
          isLoading={isLoading(importFormProgress)}
          size="sm"
          startContent={isFormFilled ? <RefreshCwIcon /> : <SparklesIcon />}
          variant={isFormFilled ? "ghost" : "solid"}
          onPress={() => {
            importFormData();
          }}
        >
          {isFormFilled
            ? translations.extractDocs.splitView.header.buttonRefreshData
            : translations.extractDocs.splitView.header.buttonImportData}
        </Button>
        <Button
          color="primary"
          endContent={<XIcon />}
          id="close-modal"
          size="sm"
          variant="light"
          onPress={onClose}
        >
          {translations.extractDocs.splitView.header.buttonClose}
        </Button>
      </div>
    </ModalHeader>
  );
};

const DocsModalContent = ({
  auction,
  auctionForms,
  consultation,
  files,
  formName,
  onAfterDelete,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  auctionForms: AuctionForm[];
  formName: DocsFormName;
  files: { name: string; file: FileState }[];
  onAfterDelete?: () => void;
  patient: Patient;
}) => {
  const translations = useTranslations();

  const {
    canImportData,
    config,
    confirmationChecked,
    footerBanner,
    footerScreen,
    form,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData,
    importFormProgress,
    methods,
    onCloseHeaderBanner,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  } = useDocsForm({
    auction,
    auctionForms,
    consultation,
    files,
    formName,
    onAfterDelete,
    patient,
  });
  const { setFormModal } = useRecareDocsContext();

  return (
    <FormProvider {...methods}>
      <Form className="flex h-full gap-0" onSubmit={handleSubmit}>
        <DocsModalHeader
          canImportData={canImportData}
          hasPreviousFormValue={hasPreviousFormValue}
          header={config.header({ translations })}
          importFormData={importFormData}
          importFormProgress={importFormProgress}
          patient={patient}
          onClose={() => {
            if (
              methods.formState.isDirty &&
              headerBannerAction.banner !== HEADER_BANNERS.FORM_DIRTY_WARNING
            ) {
              setHeaderBannerAction({
                banner: HEADER_BANNERS.FORM_DIRTY_WARNING,
              });
            } else {
              setFormModal(null);
            }
          }}
        />
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex h-full w-full flex-row gap-0 overflow-hidden p-0">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 overflow-y-auto">
              <DocsFormFields<typeof formName>
                auction={auction}
                config={config}
                formName={formName}
                headerBannerAction={headerBannerAction}
                importData={importFormData}
                importFormProgress={importFormProgress}
                shouldSkipRequiredValidation={skipRequiredValidation}
                sourceInformation={sourceInformation}
                onCloseHeaderBanner={onCloseHeaderBanner}
              />
            </div>
            <FormFooter
              canPrint={config.config.canPrint}
              canSendToHis={config.config.canSendToHis}
              footerBanner={footerBanner}
              footerScreen={footerScreen}
              form={form as AuctionForm | undefined}
              handleOpenPdf={handleOpenPdf}
              handleSendToHis={handleSendToHis}
              hasPreviousFormValue={hasPreviousFormValue}
              isConfirmationChecked={confirmationChecked}
              progressOpenPdf={progressOpenPdf}
              progressSendToHis={progressSendToHis}
              setConfirmationChecked={setConfirmationChecked}
              setFooterBanner={setFooterBanner}
              setFooterScreen={setFooterScreen}
              onBack={handleBack}
              onDelete={handleDelete}
              onDeleteProgress={handleDeleteProgress}
              onSubmitProgress={onSubmitProgress}
            />
          </div>
          <DocsModalSources
            files={files}
            formName={formName}
            patient={patient}
            resetSelectedSource={sourceInformation.resetSelectedSource}
            selectedSource={sourceInformation.selectedSource}
          />
        </ModalBody>
      </Form>
    </FormProvider>
  );
};

export const RecareDocsModal = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  patient: Patient;
}) => {
  const { formModal } = useRecareDocsContext();
  const [formKey, setFormKey] = useState(0);

  const [auctionFormsQueryProgress, auctionForms] = useGetAuctionForms({
    auctionId: auction?.id ?? 0,
    skip: formModal === null || !auction?.id,
  });

  const { files } = useFetchFiles({
    auction,
    consultation,
    patient,
  });

  if (!formModal) return null;

  return (
    <Modal
      classNames={{
        backdrop: "z-modal!",
        base: "max-h-[95vh] max-w-[1600px]! overflow-y-auto",
        wrapper: "z-modal!",
      }}
      hideCloseButton
      isDismissable={false}
      isOpen
    >
      <ModalContent className="flex h-[95vh] w-[95vw] flex-col">
        {isLoading([auctionFormsQueryProgress]) ? (
          <ModalBody className="flex h-full w-full items-center justify-center">
            <Spinner color="primary" size="lg" testId="spinner-loading-docs" />
          </ModalBody>
        ) : (
          <DocsModalContent
            key={formKey}
            auction={auction}
            auctionForms={auctionForms ?? []}
            consultation={consultation}
            files={files}
            formName={formModal}
            patient={patient}
            onAfterDelete={() => setFormKey((k) => k + 1)}
          />
        )}
      </ModalContent>
    </Modal>
  );
};
